import { makeAutoObservable } from "mobx";
import type RootStore from "stores/RootStore";

/**
 * THE one DevOps selection — which Azure DevOps connection and project the
 * boards are looking at.
 *
 * Until 2026-09-03 the Pipelines, Releases, Repos, Work Items and
 * Subscriptions stores each carried their own `selectedConnectionId` /
 * `selectedProjectId`, auto-picked `connections[0]` independently, and never
 * told each other: choosing a project on Pipelines left Repos on whatever it
 * had chosen for itself. Five copies of one fact, and the operator's
 * "re-pick the connection on every page" complaint was exactly that.
 *
 * This store is the single copy. The boards read it (and write it from their
 * headers); Project 360 sets it on enter so its tabs — the same board bodies —
 * are already scoped; the Overview's card click sets it on the way in.
 *
 * It holds SELECTION only: ids and a display name. The connection and project
 * records themselves stay in their ServiceStore caches / proxy responses, per
 * the MobX rule. Remembered per browser so a reload lands where the operator
 * left off; every storage access is guarded because storage can be absent
 * (private window, blocked site data) and a page must render without it.
 */
export interface DevOpsScope {
  connectionId: string | null;
  projectId: string | null;
  /** Display only — the project's name as last seen, so a header can show it
   *  before the proxy list for that connection has loaded. */
  projectName: string | null;
}

const STORAGE_KEY = "devops.scope";

const readStored = (): DevOpsScope | null => {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<DevOpsScope>;
    return {
      connectionId: typeof parsed.connectionId === "string" ? parsed.connectionId : null,
      projectId: typeof parsed.projectId === "string" ? parsed.projectId : null,
      projectName: typeof parsed.projectName === "string" ? parsed.projectName : null,
    };
  } catch {
    return null;
  }
};

const writeStored = (scope: DevOpsScope): void => {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(scope));
  } catch {
    // Storage is a convenience, never a requirement.
  }
};

class DevOpsScopeStore {
  rootStore: RootStore;

  connectionId: string | null = null;
  projectId: string | null = null;
  projectName: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
    const stored = readStored();
    if (stored) {
      this.connectionId = stored.connectionId;
      this.projectId = stored.projectId;
      this.projectName = stored.projectName;
    }
  }

  get scope(): DevOpsScope {
    return {
      connectionId: this.connectionId,
      projectId: this.projectId,
      projectName: this.projectName,
    };
  }

  /** True when both halves are chosen — the boards can load. */
  get isComplete(): boolean {
    return !!this.connectionId && !!this.projectId;
  }

  /**
   * Set both at once (Project 360 on enter, an Overview card click).
   *
   * A partial that moves the CONNECTION without naming a project clears the
   * project, exactly as `setConnection` does: a project id belongs to one
   * organisation, and carrying the old one over would send every board a
   * project its connection has never heard of. A partial that moves the
   * PROJECT without naming it drops the name for the same reason — a name
   * belongs to the id it was read from.
   */
  setScope(next: Partial<DevOpsScope>) {
    const connectionMoved =
      next.connectionId !== undefined && next.connectionId !== this.connectionId;
    const projectMoved =
      next.projectId !== undefined && next.projectId !== this.projectId;
    if (next.connectionId !== undefined) this.connectionId = next.connectionId;
    if (next.projectId !== undefined) this.projectId = next.projectId;
    else if (connectionMoved) this.projectId = null;
    // A name is only ever the name OF the id beside it. Moving to a different
    // project without naming it drops the old name rather than letting a
    // header label the new project with the previous one's name; whoever
    // loads the project fills it in.
    if (next.projectName !== undefined) this.projectName = next.projectName;
    else if (connectionMoved || projectMoved) this.projectName = null;
    if (this.projectId === null) this.projectName = null;
    writeStored(this.scope);
  }

  /** A connection change clears the project: a project id belongs to one org. */
  setConnection(connectionId: string | null) {
    if (connectionId === this.connectionId) return;
    this.connectionId = connectionId;
    this.projectId = null;
    this.projectName = null;
    writeStored(this.scope);
  }

  setProject(projectId: string | null, projectName: string | null = null) {
    this.projectId = projectId;
    this.projectName = projectId ? projectName : null;
    writeStored(this.scope);
  }

  /**
   * Check the remembered connection against the connections that actually
   * exist. The scope is restored from storage before any list has loaded, so
   * a connection removed since the last visit would otherwise be handed to
   * every board as-is: its project read fails, the board sits on an error
   * banner, and the auto-pick that an empty scope gets never runs.
   *
   * A stored connection that is not among `ids` clears the WHOLE scope (a
   * project id belongs to one org) and returns false, so the caller falls
   * through to its empty-scope auto-pick; otherwise nothing changes and it
   * returns true. Ids are compared case-insensitively (GUIDs arrive in either
   * case). Callers must NOT pass the list of a FAILED load: an empty list
   * from an error is not evidence that the connection is gone.
   */
  reconcileConnections(ids: readonly string[]): boolean {
    const stored = this.connectionId;
    if (!stored) return true;
    const wanted = stored.toLowerCase();
    if (ids.some((id) => id.toLowerCase() === wanted)) return true;
    this.clear();
    return false;
  }

  clear() {
    this.connectionId = null;
    this.projectId = null;
    this.projectName = null;
    writeStored(this.scope);
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   * The persisted scope goes too: it was the previous person's pick.
   */
  reset() {
    this.clear();
  }
}

export default DevOpsScopeStore;
